<script setup lang="ts">
import InputCaracterCounter from '@/components/InputCaracterCounter.vue'
import type { Audience, LoyaltyProgram } from '@/types/types'
import ConsentWordingEditor from './ConsentWordingEditor.vue'
import CustomerStatusSection from './CustomerStatusSection.vue'
import PreviewZone from './PreviewZone.vue'
import { renderWordingWithLabels } from './consentWording'

const { user } = useLocalStorage()

const { t } = useI18n()

const CGU_WORDING_MAX_LENGTH = 300

const props = defineProps<{
  hasWebappForceInfoCompletionAtRegistrer: boolean
  loyaltyProgram: LoyaltyProgram
  audiences: Audience[]
}>()

const isCustomerStatusEnabled = defineModel<boolean>('isCustomerStatusEnabled', { required: true })
const customerStatusAudienceIds = defineModel<number[]>('customerStatusAudienceIds', { required: true })

const emit = defineEmits<{
  (e: 'update:hasWebappForceInfoCompletionAtRegistrer', value: boolean): void
  (e: 'update:loyaltyProgram', value: LoyaltyProgram): void
}>()

const updateLoyaltyProgram = (field: 'cguUrl' | 'cguWording' | 'privacyPolicyUrl', value: string | number) => {
  emit('update:loyaltyProgram', {
    ...props.loyaltyProgram,
    [field]: String(value)
  })
}

const hasCguUrl = computed(() => !!props.loyaltyProgram.cguUrl?.trim())
const hasPrivacyPolicyUrl = computed(() => !!props.loyaltyProgram.privacyPolicyUrl?.trim())

const defaultWordingKey = computed(() => {
  if (hasCguUrl.value && hasPrivacyPolicyUrl.value) return 'pages.loyaltySupports.webapp.config.cguWordingDefault'
  if (hasCguUrl.value) return 'pages.loyaltySupports.webapp.config.cguWordingDefaultCguOnly'
  return 'pages.loyaltySupports.webapp.config.cguWordingDefaultPrivacyOnly'
})

const defaultWording = computed(() => {
  if (!hasCguUrl.value && !hasPrivacyPolicyUrl.value) return ''
  return t(defaultWordingKey.value, { merchantName: user.value.merchant.name })
})

const previewWording = computed(() => props.loyaltyProgram.cguWording?.trim() || defaultWording.value)

const renderedWording = computed(() =>
  renderWordingWithLabels(previewWording.value, {
    cgu: t('pages.loyaltySupports.webapp.config.cguLinkLabel'),
    privacyPolicy: t('pages.loyaltySupports.webapp.config.privacyPolicyLinkLabel')
  })
)
</script>

<template>
  <div class="flex flex-col gap-5">
    <PreviewZone zone="profileInfo" class="flex flex-col gap-5">
      <h3 class="text-base font-sans font-semibold">{{ t('pages.loyaltySupports.webapp.config.conditionLabel') }}</h3>
      <div class="flex flex-col">
        <p class="text-sm">{{ t('pages.loyaltySupports.webapp.config.conditionLabel1') }}</p>
        <BaseSwitch
          id="hasNotificationAfterOrder"
          :model-value="hasWebappForceInfoCompletionAtRegistrer"
          class="brick-switch"
          @update:model-value="emit('update:hasWebappForceInfoCompletionAtRegistrer', $event)"
        >
          <template #label>
            <p class="text-sm font-normal text-foreground-secondary">
              {{ t('pages.loyaltySupports.webapp.config.conditionLabel2') }}
            </p>
          </template>
        </BaseSwitch>
      </div>
    </PreviewZone>

    <PreviewZone zone="storeSelection" class="flex flex-col gap-5">
      <h3 class="text-base font-sans font-semibold">{{ t('pages.loyaltySupports.webapp.config.legalTitle') }}</h3>
      <div class="flex flex-col gap-5">
        <AppFormInput
          id="cguUrl"
          :model-value="loyaltyProgram.cguUrl"
          type="text"
          placeholder="https://www.exemple.com/mesconditions "
          :label="t('pages.loyaltySupports.webapp.config.cguUrl')"
          @update:model-value="updateLoyaltyProgram('cguUrl', $event)"
        />
        <AppFormInput
          id="privacyPolicyUrl"
          :model-value="loyaltyProgram.privacyPolicyUrl"
          type="text"
          placeholder="https://www.exemple.com/confidentialite"
          :label="t('pages.loyaltySupports.webapp.config.privacyPolicyUrl')"
          @update:model-value="updateLoyaltyProgram('privacyPolicyUrl', $event)"
        />
        <div class="flex flex-col gap-1.5">
          <label for="cguWording" class="text-sm font-normal text-foreground">
            {{ t('pages.loyaltySupports.webapp.config.cguWording') }}
          </label>
          <ConsentWordingEditor
            id="cguWording"
            :model-value="loyaltyProgram.cguWording"
            :max-length="CGU_WORDING_MAX_LENGTH"
            :has-cgu-url="hasCguUrl"
            :has-privacy-policy-url="hasPrivacyPolicyUrl"
            :default-wording="defaultWording"
            @update:model-value="updateLoyaltyProgram('cguWording', $event)"
          />
          <InputCaracterCounter :content="renderedWording" :max-length="CGU_WORDING_MAX_LENGTH" />
        </div>
      </div>
    </PreviewZone>

    <CustomerStatusSection
      v-model:is-enabled="isCustomerStatusEnabled"
      v-model:audience-ids="customerStatusAudienceIds"
      :audiences="audiences"
    />
  </div>
</template>
